import React, { useCallback, useState } from "react"
import { getAuth, sendPasswordResetEmail } from "firebase/auth"
import URLUtil from "../../utils/URLUtils"
import routes from "../../../routes"
import { useFirebaseAuthError } from "../../components/hooks/useFirebaseAuthError"
import { useLoading } from "../../components/hooks/useLoading"
import { Auth as AuthMessage } from "../../config/messages"
import { useForm } from "react-hook-form"
import { logout, useUser } from "../../utils/auth/useUser"
import HTMLHead from "../../components/utils/HTMLHead"
import clsx from "clsx"
import { useTieupToast } from "src/components/hooks/useTieupToast"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"

type Props = {
  email: string
}

export const PasswordNew: React.FC = () => {
  const [email, setEmail] = useState("")
  const { register, errors, handleSubmit } = useForm<Props>()
  const { showFirebaseError } = useFirebaseAuthError()
  const { fullScreenLoading } = useLoading()
  const [isMailSend, setIsMailSend] = useState(false)
  const { isMember, firebaseUser } = useUser()
  const { addToastError } = useTieupToast()

  const handleChangeEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value)
    },
    [],
  )

  const onSubmit = async (values: Props) => {
    try {
      fullScreenLoading(true)

      /** メンバーの場合はメアドが正しいかバリデーション実行 */
      if (isMember) {
        if (!firebaseUser || firebaseUser.email !== values.email) {
          addToastError("登録されたメールアドレスと異なります")

          return
        }
      }

      const auth = getAuth()
      const redirectURL = `${URLUtil.getRootURL()}${routes.authSignIn()}`
      await sendPasswordResetEmail(auth, values.email, {
        url: redirectURL,
        handleCodeInApp: false,
      })
      await logout()
      setIsMailSend(true)
    } catch (error) {
      showFirebaseError(error)
    } finally {
      fullScreenLoading(false)
    }
  }

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="auth/password-new" />
      <main>
        <header className="p-signIn-header">
          <h1 className="p-signIn-header-title">パスワードの再設定</h1>
        </header>
        {/* メール送信前 */}
        {!isMailSend && (
          <section className="p-signIn-section">
            <div className="p-signIn-section-inner">
              <h2 className="p-signIn-section-message">
                再設定用の確認メールを
                <br className="md:hidden lg:hidden" />
                ご指定のアドレスに送信いたしますので
                <br />
                ご登録のメールアドレスをご入力ください。
              </h2>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="p-signIn-block">
                  <label htmlFor="input-email" className="c-label-primary">
                    登録メールアドレス
                  </label>
                  <input
                    id="input-email"
                    className={clsx({
                      "c-textfield-primary": true,
                      "-error": errors.email,
                    })}
                    type="email"
                    name="email"
                    ref={register({ required: true })}
                    onChange={handleChangeEmail}
                  />
                  {errors.email && (
                    <p className="c-note-primary -error">
                      {AuthMessage.error.email}
                    </p>
                  )}
                </div>
                <div className="p-button">
                  <button className="c-button-secondary -full" type="submit">
                    送信
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}
        {/* メール送信後 */}
        {isMailSend && (
          <section className="p-signIn-section">
            <div className="p-signIn-section-inner">
              <h2 className="p-signIn-section-title">
                ご登録のアドレスにメールを送信しました。
              </h2>
              <p>
                ご登録いただいた{email}
                に確認用メールを送信いたしましたので、内容をご確認いただき、パスワードの再設定ををお願いいたします。
              </p>
              <p className="c-note-primary">
                10分経っても届かない場合はinformation@tieupnovels.comからのメールの受信を許可していただき、再度パスワードの再設定を申請ください。
              </p>
              <p className="p-signIn-link">
                <a href="/" className="p-signIn-link-anchor">
                  トップページへ戻る
                </a>
              </p>
            </div>
          </section>
        )}
      </main>
    </DefaultLayoutForReader>
  )
}

export default PasswordNew
